import { z } from "zod";
import { CompletionWindow } from "./fact.js";
import { Accession, Cik, IsoDate, Ratio, Timestamp } from "./primitives.js";
import { CompanyRef } from "./company.js";
/**
 * The arc a separation moves through. Stage is derived from evidence — a situation
 * advances only when the filing proving the next stage exists — and is never set by
 * hand.
 */
export const Stage = z.enum([
    "announced", // intent stated (8-K 7.01/8.01); no registration statement
    "form10_filed", // 10-12B (or S-1/S-4) on file
    "terms_set", // distribution ratio AND record date both known
    "when_issued", // 8-A12B filed; when-issued market forming
    "completed", // distribution has occurred
    "abandoned", // called off, or converted to an outright sale
]);
/** Ordered for progress display. `abandoned` is a terminal off-ramp, not a step. */
export const STAGE_ORDER = [
    "announced", "form10_filed", "terms_set", "when_issued", "completed",
];
/** The kind of special situation; spin-offs are the only kind. */
export const SituationKind = z.enum(["spinoff"]);
/**
 * The owner's judgement about a situation. `stage` is derived from evidence and
 * `isPinned` is attention; this is what the owner concluded.
 *
 * `not_a_situation` means the separation was never real, and is the only value that
 * stops a situation matching new filings, so a later filing by that company opens a new
 * one. `not_interesting` means the separation is real and the owner has passed on it, so
 * a later filing about it still attaches here.
 */
export const SituationDisposition = z.enum([
    "unreviewed",
    "not_a_situation",
    "not_interesting",
    "watching",
]);
/**
 * The two judgements that hide a situation from the radar by default, and nothing else.
 * The radar still filters them separately: "the screen was wrong" and "the screen was
 * right and I passed" are different findings to review.
 */
export const DISMISSED_DISPOSITIONS = [
    "not_a_situation",
    "not_interesting",
];
/**
 * How the filings say the separation is taxed: tax-free under Section 355, taxable, or a
 * Reverse Morris Trust; `unknown` until one says.
 */
export const TaxTreatment = z.enum(["section_355", "taxable", "rmt", "unknown"]);
/** Why a filing is attached to a situation — drives grouping in the UI. */
export const FilingRole = z.enum([
    "announcement", "registration", "amendment", "terms",
    "listing", "insider", "periodic", "proxy", "other",
]);
/**
 * What `parent.name` says when there is no parent to name. Shared so the web can tell it
 * apart from a parent named in prose with no CIK behind it.
 */
export const UNKNOWN_PARENT = "(parent not identified)";
/**
 * The placeholder a parent-opened situation carries until something names its SpinCo.
 * Shared so the web can compare against it.
 */
export const UNNAMED_SPINCO = "(not yet named)";
/** The row shape the radar's index column renders, complete enough to need no second call. */
export const SituationSummary = z.object({
    id: z.string().uuid(),
    kind: SituationKind,
    stage: Stage,
    parent: CompanyRef,
    spinco: CompanyRef,
    ratio: Ratio.nullable(),
    announcedOn: IsoDate.nullable(),
    recordDate: IsoDate.nullable(),
    distributionDate: IsoDate.nullable(),
    /** Server-computed so every client agrees. Null when no distribution date is known. */
    daysToDistribution: z.number().int().nullable(),
    /**
     * The window a separation is expected to complete in, when no exact date is set.
     * Never merged into `distributionDate`, which drives the countdown and means a date
     * the filing committed to.
     */
    expectedCompletion: CompletionWindow.nullable(),
    taxTreatment: TaxTreatment,
    flags: z.array(z.object({
        id: z.string().uuid(),
        kind: z.string(),
        severity: z.enum(["info", "watch", "critical"]),
        summary: z.string(),
    })),
    latestFiling: z
        .object({ accession: Accession, formType: z.string(), filingDate: IsoDate })
        .nullable(),
    isPinned: z.boolean(),
    isArchived: z.boolean(),
    /**
     * On the summary because list views show each row's disposition and its reason.
     */
    disposition: SituationDisposition,
    dispositionReason: z.string().nullable(),
    dispositionAt: Timestamp.nullable(),
    stageChangedAt: Timestamp.nullable(),
    updatedAt: Timestamp,
});
/** One situation as its own page shows it: the summary row plus identities, counts and warnings. */
export const SituationDetail = SituationSummary.extend({
    /**
     * Either side may be unknown, but not both. A `10-12B` is filed by the SpinCo, so the
     * parent's CIK is in no filing header.
     */
    parentCik: Cik.nullable(),
    spincoCik: Cik.nullable(),
    confidence: z.number().min(0).max(1),
    counts: z.object({
        filings: z.number().int().nonnegative(),
        facts: z.number().int().nonnegative(),
        excerpts: z.number().int().nonnegative(),
        openQuestions: z.number().int().nonnegative(),
        notes: z.number().int().nonnegative(),
    }),
    /**
     * Tolerant-parse record for this situation. Surfaced so a systematic extraction
     * failure reads as a warning count rather than as an empty, apparently-fine page.
     */
    warnings: z.array(z.object({ message: z.string(), createdAt: Timestamp })),
    createdAt: Timestamp,
});
/** One dated entry on a situation's timeline, past or expected. */
export const SituationEvent = z.object({
    id: z.string().uuid(),
    situationId: z.string().uuid(),
    occurredOn: IsoDate,
    kind: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    accession: Accession.nullable(),
    /** A projected future step (a distribution date not yet reached) renders unfilled. */
    isExpected: z.boolean(),
});
/**
 * Owner-editable fields only. Derived values are corrected via a fact override
 * (`PUT /api/situations/:id/facts/:kind`), so a later extraction cannot overwrite them;
 * that is why `spincoTicker` is not here.
 *
 * `spincoName` is here so the owner can replace the `UNNAMED_SPINCO` placeholder. It is
 * also stored as a `user` fact, so a later extraction does not overwrite it.
 */
export const SituationPatch = z.object({
    isPinned: z.boolean().optional(),
    isArchived: z.boolean().optional(),
    /**
     * The owner's judgement. `not_a_situation` stops reconciliation matching new filings
     * against this situation. `unreviewed` clears the reason and timestamp with it.
     */
    disposition: SituationDisposition.optional(),
    /**
     * Free-form. Sent alone, it edits the reason on the existing judgement; it is rejected
     * on a situation that is or becomes `unreviewed`.
     */
    dispositionReason: z.string().max(2000).nullable().optional(),
    spincoName: z.string().min(1).optional(),
});
/**
 * Folding duplicate situations into the one that survives. The owner names the keeper
 * and the duplicates; nothing merges automatically.
 */
export const MergeRequest = z.object({
    /** The situation that survives and absorbs the others. */
    keeperId: z.string().uuid(),
    /** The duplicates, dismissed as `not_a_situation` with their filings and facts moved. */
    mergeIds: z.array(z.string().uuid()).min(1).max(50),
    /**
     * Why, recorded on every merged row. Optional: approving a proposal sends its stated
     * reasoning, and the server falls back to a description of what it did.
     */
    reason: z.string().max(2000).optional(),
    /**
     * Whether the owner approved the proposal as offered rather than changing it first.
     */
    approvedProposal: z.boolean().optional(),
});
//# sourceMappingURL=situation.js.map